import { AxiosResponse } from "axios";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { DataTable, DataTableSortEvent } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { PaginatorPageChangeEvent } from "primereact/paginator";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "../../../components/loader";
import PrimePaginator from "../../../components/prime-paginator";
import { APIResponseEntity } from "../../../interface/api-responce";
import { GetOfficeDDLEntity } from "../../../interface/offices";
import { CityDDLEntity, CountryDDLEntity, StateDDLEntity, UsersDDlEntity } from "../../../interface/order";
import { DraftWorkOrderEntity, FetchDraftWorkOrdersAPIRespEntity, FetchDraftWorkOrdersReqEntity } from "../../../interface/production-planning/DraftWorkOrderEntity";
import { APIDraftOrderListing, APIFetchCityForWorkOrderDDL, APIGetCountryDDL, APIGetOfficeDDLV2, APIGetStateForWorkOrderDDL, APIGetUserForProductionDDl } from "../../../service/api-service";
import { checkActionAuthorization } from "../../../service/shared.service";
import { toastOptObj } from "../../../utils/constants";
import { IsNullOrEmptyArray } from "../../../utils/null-check";
import LastUpdatedDateFilter from "../components/LastUpdatedDateFilter";
import "./draft-work-order.css";
import ExportDraftWOs from "./export-draft-WOs";

function DraftWorkOrders() {

    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [draftWorkOrders, setDraftWorkOrders] = useState<DraftWorkOrderEntity[]>([]);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [usersFilterDDL, setUsersFilterDDL] = useState<UsersDDlEntity[] | []>([]);
    const [officesDDL, setOfficesDDL] = useState<GetOfficeDDLEntity[] | []>([]);
    const [countryDDL, setCountryDDL] = useState<CountryDDLEntity[] | []>([]);
    const [stateDDL, setStateDDL] = useState<StateDDLEntity[] | []>([]);
    const [cityDDL, setCityDDL] = useState<CityDDLEntity[] | []>([]);

    const [filterReq, setFilterReq] = useState<FetchDraftWorkOrdersReqEntity>({
        orderReferenceNumber: "",
        projectName: "",

        salesHeadIds: [],
        officeIds: [],

        countryIds: [],
        stateIds: [],
        cityIds: [],

        lastUpdatedFrom: "",
        lastUpdatedTo: "",

        pageNumber: 0,
        pageSize: 10,

        sortColumn: "updatedAt",
        sortOrder: "desc"
    });

    useEffect(() => {
        fetchDraftWorkOrders();
    }, [filterReq]);

    useEffect(() => {
        fetchUsersDDl();
        fetchCountriesDDL();
        fetchOfficeDDL();
    }, []);

    useEffect(() => {
        if (filterReq.countryIds && !IsNullOrEmptyArray(filterReq.countryIds)) {
            fetchStatesDDL();
        } else {
            setStateDDL([]);
        }
    }, [filterReq.countryIds]);

    useEffect(() => {
        if (filterReq.stateIds && !IsNullOrEmptyArray(filterReq.stateIds)) {
            fetchCitiesDDL()
        }
        else {
            setCityDDL([]);
        }
    }, [filterReq.stateIds]);

    const fetchDraftWorkOrders = async (
        requestFilter: FetchDraftWorkOrdersReqEntity = filterReq
    ): Promise<void> => {

        let filterObj: FetchDraftWorkOrdersReqEntity = {
            ...requestFilter,
            pageNumber: requestFilter.pageNumber + 1
        };

        setIsLoading(true);

        const resp: AxiosResponse = await APIDraftOrderListing(filterObj);

        setIsLoading(false);

        if (!resp || resp.status !== 200 || !resp.data) {
            return;
        }

        const apiRespPayload: APIResponseEntity = resp.data;

        if (
            apiRespPayload.statusCode !== 200 ||
            !apiRespPayload.data
        ) {
            toast.error(
                apiRespPayload.message || apiRespPayload.errorMsg
            );
            return;
        }

        const responseData: FetchDraftWorkOrdersAPIRespEntity =
            apiRespPayload.data;

        setDraftWorkOrders(responseData.data);
        setTotalRecords(responseData.totalCount);
    };

    const fetchUsersDDl = async (): Promise<void> => {
        const resp: AxiosResponse<APIResponseEntity> = await APIGetUserForProductionDDl([]);

        if (resp) {
            if (resp && resp.data && resp.status === 200) {
                const axiosResp: APIResponseEntity = resp.data;

                if (
                    axiosResp &&
                    axiosResp.data &&
                    axiosResp.status &&
                    axiosResp.statusCode === 200
                ) {
                    axiosResp.data.data.sort((a: UsersDDlEntity, b: UsersDDlEntity) =>
                        a.userName.localeCompare(b.userName)
                    );
                    setUsersFilterDDL(axiosResp.data.data);
                }
            } else {
                toast.error(resp.data.message, { ...toastOptObj });
            }
        }
    };

    const fetchOfficeDDL = async (): Promise<void> => {
        const resp: AxiosResponse = await APIGetOfficeDDLV2([]);

        if (!resp || (resp && (resp.status !== 200 || !resp.data))) { return; }

        const apiRespPayload: APIResponseEntity = resp.data;

        if (apiRespPayload.statusCode !== 200 || !apiRespPayload.data) {
            toast.error(apiRespPayload.message || apiRespPayload.errorMsg,
                { ...toastOptObj });
            return;
        }

        const DDLData: GetOfficeDDLEntity[] | [] = apiRespPayload.data.data;
        setOfficesDDL(DDLData);
    }

    async function fetchCitiesDDL() {
        //  setIsLoading(true);
        const resp: AxiosResponse<APIResponseEntity> =
            await APIFetchCityForWorkOrderDDL(filterReq.stateIds.join(","), []);
        // setIsLoading(false);

        if (!resp || !resp.data || resp.status !== 200) {
            return;
        }

        const axiosResp: APIResponseEntity = resp.data;
        if (
            !axiosResp ||
            !axiosResp.data ||
            !axiosResp.status ||
            axiosResp.statusCode !== 200
        ) {
            toast.error(axiosResp.message, { ...toastOptObj });
            return;
        }
        setCityDDL(axiosResp.data.data);
    }

    async function fetchStatesDDL() {
        const resp: AxiosResponse<APIResponseEntity> =
            await APIGetStateForWorkOrderDDL([]);
        // setIsLoading(false);

        if (!resp || !resp.data || resp.status !== 200) {
            return;
        }

        const axiosResp: APIResponseEntity = resp.data;
        if (
            !axiosResp ||
            !axiosResp.data ||
            !axiosResp.status ||
            axiosResp.statusCode !== 200
        ) {
            toast.error(axiosResp.message, { ...toastOptObj });
            return;
        }
        setStateDDL(axiosResp.data.data.sort((a: StateDDLEntity, b: StateDDLEntity) =>
            a.name.localeCompare(b.name)
        ));
    }

    async function fetchCountriesDDL() {
        const resp: AxiosResponse<APIResponseEntity> =
            await APIGetCountryDDL();

        if (!resp || !resp.data || resp.status !== 200) {
            return;
        }

        const axiosResp: APIResponseEntity = resp.data;
        if (
            !axiosResp ||
            !axiosResp.data ||
            !axiosResp.status ||
            axiosResp.statusCode !== 200
        ) {
            toast.error(axiosResp.message, { ...toastOptObj });
            return;
        }

        setCountryDDL(axiosResp.data.data);
    }

    const onPageChange = (event: PaginatorPageChangeEvent) => {
        setFilterReq({
            ...filterReq,
            pageNumber: event.page,
            pageSize: event.rows
        });
    };

    const onTableSort = (e: DataTableSortEvent): void => {
        const meta = e ? e : null;
        if (meta && meta.sortField && meta.sortOrder) {

            const sortColumn = meta.sortField;

            setFilterReq(prev => ({
                ...prev, sortColumn, pageNumber: 0,
                sortOrder:
                    prev.sortOrder === "asce" && meta.sortOrder === 1 ? 'desc' :
                        prev.sortOrder === "desc" && meta.sortOrder === 1 ? 'asce' :
                            prev.sortOrder === "asce" && meta.sortOrder === -1 ? 'desc' :
                                prev.sortOrder === "desc" && meta.sortOrder === -1 ? 'asce' : "asce"
            }));
        }
    };
    // const onTableSort = (e: DataTableSortEvent): void => {
    //     if (!e.sortField) return;

    //     setFilterReq({
    //         ...filterReq,
    //         pageNumber: 0,
    //         sortColumn: String(e.sortField),
    //         sortOrder: e.sortOrder === 1 ? "asce" : "desc"
    //     });
    // };

    const clearAllFilter = (): void => {
        setFilterReq({
            orderReferenceNumber: "",
            projectName: "",

            salesHeadIds: [],
            officeIds: [],

            countryIds: [],
            stateIds: [],
            cityIds: [],

            lastUpdatedFrom: "",
            lastUpdatedTo: "",

            pageNumber: 0,
            pageSize: 10,

            sortColumn: "updatedAt",
            sortOrder: "desc"
        });
    };

    const commonSearchFilter = (field: string) => {
        return (
            <InputText
                value={filterReq[field as keyof typeof filterReq] as string}
                onChange={(e) =>
                    setFilterReq({
                        ...filterReq,
                        pageNumber: 0,
                        [field]: e.target.value
                    })
                }
            />
        );
    };

    const actionBody = (row: DraftWorkOrderEntity) => {
        return (
            <ul className="tableIcon cursor-pointer d-flex">
                {
                    checkActionAuthorization("view", "Draft WOs") &&
                    <li
                        title="View Details"
                        onClick={() =>
                            navigate(`/draft-work-order-details/view/${row.orderReferenceNumber}`)
                        }
                    >
                        <img src="/assets/images/eye.svg" alt="View" />
                    </li>
                }
            </ul>
        );
    };

    const [tempSalesHead, setTempSalesHead] = useState<string[]>(filterReq.salesHeadIds || [])
    const salesMultiSelectRef = useRef<any>(null);

    useEffect(() => {
        setTempSalesHead(filterReq.salesHeadIds || []);
    }, [filterReq.salesHeadIds]);

    const filterTemplateSalesHead = (
        options: ColumnFilterElementTemplateOptions
    ) => {
        const panelFooterTemplate = () => (
            <div className="flex justify-between p-2">
                <button
                    className="p-button p-component p-button-sm"
                    onClick={() => {
                        setFilterReq((prev) => ({
                            ...prev,
                            salesHeadIds: tempSalesHead,
                            pageNumber: 0,
                        }));

                        salesMultiSelectRef.current.hide();
                    }}
                    disabled={
                        JSON.stringify(tempSalesHead) ===
                        JSON.stringify(filterReq.salesHeadIds)
                    }
                >
                    Apply
                </button>
            </div>
        );

        return (
            <MultiSelect
                ref={salesMultiSelectRef}
                value={tempSalesHead}
                onChange={(e) => setTempSalesHead(e.value)}
                options={usersFilterDDL}
                optionLabel="userFullName"
                optionValue="userId"
                placeholder="Select Sales Head"
                resetFilterOnHide
                maxSelectedLabels={1}
                className="w-full md:w-20rem"
                filter
                panelFooterTemplate={panelFooterTemplate}
                onHide={() =>
                    setTempSalesHead(filterReq.salesHeadIds || [])
                }
            />
        );
    };

    const [tempOffice, setTempOffice] = useState<string[]>(filterReq.officeIds || []);
    const officeMultiSelectRef = useRef<any>(null)

    useEffect(() => {
        setTempOffice(filterReq.officeIds || []);
    }, [filterReq.officeIds]);

    const filterTemplateOfficeName = (options: ColumnFilterElementTemplateOptions) => {
        const panelFooterTemplate = () => (
            <div className="flex justify-between p-2">
                <button
                    className="p-button p-component p-button-sm"
                    onClick={() => {
                        setFilterReq(prev => ({
                            ...prev,
                            officeIds: tempOffice,
                            pageNumber: 0
                        }));
                        officeMultiSelectRef.current.hide();
                    }}
                    disabled={JSON.stringify(tempOffice) === JSON.stringify(filterReq.officeIds)}
                >
                    Apply
                </button>
            </div>
        );
        return (
            <MultiSelect
                ref={officeMultiSelectRef}
                value={tempOffice}
                onChange={(e) => setTempOffice(e.value)}
                options={officesDDL}
                optionLabel="officeName"
                optionValue="officeId"
                resetFilterOnHide
                filter
                maxSelectedLabels={1}
                panelFooterTemplate={panelFooterTemplate}
                className="w-full md:w-20rem"
                placeholder="Select offices"
                onHide={() => setTempOffice(filterReq.officeIds || [])}
            />
        );
    };

    const [tempCountry, setTempCountry] = useState<number[]>(filterReq.countryIds || []);
    const countryMultiSelectRef = useRef<any>(null);

    useEffect(() => {
        setTempCountry(filterReq.countryIds || []);
    }, [filterReq.countryIds]);

    const filterTemplateForCountry = () => {

        const panelFooterTemplate = () => (
            <div className="flex justify-between p-2">
                <button
                    className="p-button p-component p-button-sm"
                    onClick={() => {
                        setFilterReq(prev => ({
                            ...prev,
                            countryIds: tempCountry,
                            stateIds: [],
                            cityIds: [],
                            pageNumber: 0
                        }));
                        countryMultiSelectRef.current.hide();
                    }}
                    disabled={JSON.stringify(tempCountry) === JSON.stringify(filterReq.countryIds)}
                >
                    Apply
                </button>
            </div>
        );
        return (
            <MultiSelect
                ref={countryMultiSelectRef}
                value={tempCountry}
                onChange={(e) => setTempCountry(e.value)}
                options={countryDDL}
                optionLabel="name"
                optionValue="id"
                resetFilterOnHide
                filter
                maxSelectedLabels={1}
                panelFooterTemplate={panelFooterTemplate}
                className="w-full md:w-20rem"
                placeholder="Select countries"
                onHide={() => setTempCountry(filterReq.countryIds || [])}
            />
        );
    };

    const [tempState, setTempState] = useState<number[]>(filterReq.stateIds || []);

    const stateMultiSelectRef = useRef<any>(null);

    useEffect(() => {
        setTempState(filterReq.stateIds || []);
    }, [filterReq.stateIds]);

    const filterTemplateForState = (options: any) => {
        const panelFooterTemplate = () => (
            <div className="flex justify-between p-2">
                <button
                    className="p-button p-component p-button-sm"
                    onClick={() => {
                        setFilterReq(prev => ({
                            ...prev,
                            stateIds: tempState,
                            city: [],
                            pageNumber: 0
                        }));
                        stateMultiSelectRef.current.hide();
                    }}
                    disabled={JSON.stringify(tempState) === JSON.stringify(filterReq.stateIds)}
                >
                    Apply
                </button>
            </div>
        );
        return (
            <MultiSelect
                ref={stateMultiSelectRef}
                value={tempState}
                onChange={(e) => setTempState(e.value)}
                options={stateDDL}
                optionLabel="name"
                optionValue="id"
                resetFilterOnHide
                filter
                maxSelectedLabels={1}
                panelFooterTemplate={panelFooterTemplate}
                className="p-column-filter"
                placeholder="Select states"
                onHide={() => setTempState(filterReq.stateIds || [])}
                disabled={!tempCountry || tempCountry.length === 0}
            />
        );
    };

    const [tempCity, setTempCity] = useState<number[]>(filterReq.cityIds || []);
    const cityMultiSelectRef = useRef<any>(null);

    useEffect(() => {
        setTempCity(filterReq.cityIds || []);
    }, [filterReq.cityIds]);

    const filterTemplateForCity = (options: any) => {
        const panelFooterTemplate = () => (
            <div className="flex justify-between p-2">
                <button
                    className="p-button p-component p-button-sm"
                    onClick={() => {
                        setFilterReq(prev => ({
                            ...prev,
                            cityIds: tempCity,
                            pageNumber: 0
                        }));
                        cityMultiSelectRef.current.hide();
                    }}
                    disabled={JSON.stringify(tempCity) === JSON.stringify(filterReq.cityIds)}
                >
                    Apply
                </button>
            </div>
        );
        return (
            <MultiSelect
                ref={cityMultiSelectRef}
                value={tempCity}
                onChange={(e) => setTempCity(e.value)}
                options={cityDDL}
                optionLabel="name"
                optionValue="id"
                resetFilterOnHide
                filter
                maxSelectedLabels={1}
                panelFooterTemplate={panelFooterTemplate}
                className="p-column-filter"
                placeholder="Select cities"
                onHide={() => setTempCity(filterReq.cityIds || [])}
                disabled={!tempState || tempState.length === 0}
            />
        );
    };

    const filterTemplateLastUpdated = () => {
        return (
            <LastUpdatedDateFilter
                startDate={filterReq.lastUpdatedFrom}
                endDate={filterReq.lastUpdatedTo}
                updateDateCB={(from, to) => {
                    setFilterReq((prev) => ({
                        ...prev,
                        lastUpdatedFrom: from,
                        lastUpdatedTo: to,
                        pageNumber: 0,
                    }));
                }}
            />
        );
    };

    const totalSqFtSum = draftWorkOrders.reduce(
        (sum, row) => sum + (row.totalSqFt || 0), 0
    );
    const woTotalSum = draftWorkOrders.reduce(
        (sum, row) => sum + (row.woTotal || 0), 0
    );

    return (
        <>
            <Loader isLoading={isLoading} />
            <div className="content-wrapper payment-content-wrapper">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card card-rounded">
                            <div className="card-body">
                                <div className="row mb-3">
                                    <div className="col-lg-6 col-md-6 col-12">
                                        <h4 className="card-title">Draft Work Orders</h4>
                                    </div>

                                    <div className="col-12 col-md-6 col-lg-6 text-md-end text-lg-end mt-md-0">
                                        <span className='me-2 fw-bold text-primary cursor-pointer' onClick={clearAllFilter}> Clear All Filter </span>

                                        <ExportDraftWOs type="bulkAction" orderReferenceNumber={null} />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-lg-12 col-md-12 mb-3">
                                        <DataTable
                                            value={draftWorkOrders}
                                            filterDisplay="row" lazy
                                            sortField={filterReq.sortColumn}
                                            sortOrder={filterReq.sortOrder === "asce" ? 1 : -1}
                                            onSort={(e) => onTableSort(e)}
                                        >

                                            <Column field="officeName" header="OFFICE NAME"
                                                showFilterMenu={false}
                                                filter
                                                sortable
                                                filterElement={filterTemplateOfficeName}
                                            />

                                            <Column
                                                header="Order Version"
                                                field="orderWithVersion"
                                                sortable
                                                filter sortField="displayOrderReferenceNumber"
                                                showFilterMenu={false}
                                                filterElement={() => commonSearchFilter("orderReferenceNumber")}
                                            />

                                            <Column
                                                header="WO Number"
                                                field="woNumber"
                                            />

                                            <Column
                                                header="Project Name"
                                                field="projectName"
                                                sortable
                                                filter
                                                showFilterMenu={false}
                                                filterElement={() => commonSearchFilter("projectName")}
                                            />


                                            <Column
                                                field="salesHead"
                                                header="SALES HEAD"
                                                sortable
                                                filter
                                                showFilterMenu={false}
                                                filterElement={filterTemplateSalesHead}
                                            />

                                            <Column
                                                header="Locations"
                                                field="locationCount"
                                                sortable
                                            />

                                            <Column
                                                header="Total Sq Ft"
                                                field="totalSqFt"
                                                sortable
                                                filter
                                                showFilterMenu={false}
                                                filterElement={() => (
                                                    <span className="p-column-filter">
                                                        {totalSqFtSum.toLocaleString()}
                                                    </span>
                                                )}
                                            />

                                            <Column
                                                header="WO Total"
                                                field="woTotal"
                                                sortable
                                                body={(row) => (
                                                    <span style={{ whiteSpace: 'nowrap' }}>
                                                        {row.woTotal ? row.woTotal.toLocaleString() : '-'}
                                                    </span>
                                                )}
                                                filter
                                                showFilterMenu={false}
                                                filterElement={() => (
                                                    <span className="p-column-filter">
                                                        {woTotalSum.toLocaleString()}
                                                    </span>
                                                )}
                                            />

                                            <Column field="country" header="COUNTRY"
                                                showFilterMenu={false}
                                                filter
                                                filterElement={filterTemplateForCountry}
                                            />

                                            <Column field="state" header="STATE"
                                                showFilterMenu={false}
                                                filter
                                                filterElement={filterTemplateForState}
                                            />

                                            <Column field="city" header="CITY"
                                                showFilterMenu={false}
                                                filter
                                                filterElement={filterTemplateForCity}
                                            />

                                            <Column
                                                header="Last Updated"
                                                field="lastUpdatedDate"
                                                sortField="updatedAt"
                                                sortable
                                                filter
                                                showFilterMenu={false}
                                                filterElement={filterTemplateLastUpdated}
                                                body={(row) => row.lastUpdatedDate || "-"}
                                            />

                                            <Column
                                                header="Action"
                                                body={actionBody}
                                            />

                                        </DataTable>

                                        {draftWorkOrders.length > 0 && (
                                            <PrimePaginator
                                                onPageChange={onPageChange}
                                                totalRecords={totalRecords}
                                                pageNumber={filterReq.pageNumber}
                                                pageSize={filterReq.pageSize}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default DraftWorkOrders